import * as PIXI from "pixi.js";

let buttonClickHandler = null;
let uiTextScale = 1;
let suppressButtonTapUntil = 0;

export function setButtonClickHandler(handler) {
  buttonClickHandler = handler;
}

export function setUiTextScale(scale) {
  uiTextScale = Math.max(0.8, Math.min(1.2, Number(scale) || 1));
}

export function suppressButtonTap(duration = 250) {
  suppressButtonTapUntil = Math.max(suppressButtonTapUntil, performance.now() + duration);
}

export function text(label, style = {}) {
  const { fontSize = 14, ...rest } = style;
  return new PIXI.Text({
    text: label,
    style: {
      fontFamily: "Arial",
      fontSize: Math.round(fontSize * uiTextScale),
      fill: 0xe9ddc2,
      ...rest,
    },
  });
}

export function panel(width, height, options = {}) {
  const graphic = new PIXI.Graphics();
  graphic.roundRect(0, 0, width, height, options.radius ?? 6)
    .fill({ color: options.fill ?? 0x11120f, alpha: options.alpha ?? 0.88 })
    .stroke({ color: options.stroke ?? 0x7b643d, alpha: options.strokeAlpha ?? 0.82, width: options.strokeWidth ?? 1 });
  return graphic;
}

export function button(label, width, height, onPress, options = {}) {
  const container = new PIXI.Container();
  container.eventMode = "static";
  container.cursor = "pointer";
  container.hitArea = new PIXI.Rectangle(0, 0, width, height);
  const bg = panel(width, height, {
    fill: options.fill ?? 0x2a2117,
    stroke: options.stroke ?? 0x967341,
    alpha: options.alpha ?? 0.95,
    radius: 5,
  });
  const labelText = text(label, {
    fontSize: options.fontSize ?? 13,
    fill: options.fillText ?? 0xf3dfad,
    fontWeight: "700",
  });
  labelText.anchor.set(0.5);
  labelText.position.set(width / 2, height / 2);
  container.addChild(bg, labelText);
  const consume = (event) => event.stopPropagation?.();
  const defaultTextColor = options.fillText ?? 0xf3dfad;
  container.on("pointerdown", (event) => {
    if (event.pointerType !== "touch") consume(event);
    bg.tint = 0xffa83d;
    labelText.style.fill = 0xffffff;
  });
  container.on("pointerup", (event) => {
    if (event.pointerType !== "touch") consume(event);
    bg.tint = 0xffc85f;
    labelText.style.fill = 0xfff4cf;
  });
  container.on("pointerover", (event) => {
    consume(event);
    bg.tint = 0xffc85f;
    labelText.style.fill = 0xfff4cf;
  });
  container.on("pointerout", (event) => {
    consume(event);
    bg.tint = 0xffffff;
    labelText.style.fill = defaultTextColor;
  });
  container.on("pointertap", (event) => {
    consume(event);
    if (performance.now() < suppressButtonTapUntil) return;
    if (onPress && options.sound !== false) {
      buttonClickHandler?.();
    }
    onPress?.();
  });
  container.widthHint = width;
  container.heightHint = height;
  return container;
}

export function resourceBar(label, current, max, color, width = 180) {
  const container = new PIXI.Container();
  const safeMax = Math.max(1, Number(max) || 1);
  const safeCurrent = Math.max(0, Math.min(safeMax, Number(current) || 0));
  const ratio = safeCurrent / safeMax;
  const title = text(label, { fontSize: 12, fill: 0xcdbf9b, fontWeight: "700" });
  const value = text(`${safeCurrent}/${safeMax}`, { fontSize: 12, fill: 0xe9ddc2 });
  value.anchor.set(1, 0);
  value.x = width;
  const track = new PIXI.Graphics();
  track.roundRect(0, 19, width, 8, 4).fill({ color: 0x2d241e, alpha: 0.95 }).stroke({ color: 0x070605, width: 1 });
  const fill = new PIXI.Graphics();
  fill.roundRect(1, 20, Math.max(2, (width - 2) * ratio), 6, 3).fill(color);
  container.addChild(title, value, track, fill);
  return container;
}

export function clear(container) {
  if (!container) {
    return;
  }
  container.removeChildren().forEach((child) => child.destroy?.({ children: true }));
}
